import api from "api/api";
import serverCall from "api/serverCall";
import {
  createContext,
  useReducer,
  ReactElement,
  useContext,
  useEffect,
} from "react";
import {
  SET_KITE_CONNECT_STATUS,
  SET_SERVER_HEALTH_DATA,
} from "store/reducers/actions";
import { useFirebaseContext } from "./FirebaseContext";
import { ServerStats } from "types/stats";
import healthReducer, { initialState } from "store/reducers/health";
import { ServerHealthContextType } from "types/data";

const ServerHealthContext = createContext<ServerHealthContextType | null>(null);

export const ServerHealthContextProvider = ({
  children,
}: {
  children: ReactElement;
}) => {
  const { isLoggedIn } = useFirebaseContext();
  const [state, dispatch] = useReducer(healthReducer, initialState);

  const fetchServerHealthData = async () => {
    try {
      const response = await serverCall.get<ServerStats>(api.getServerHealth());
      dispatch({
        type: SET_KITE_CONNECT_STATUS,
        payload: {
          kiteStatus: response?.tickConnected ?? false,
        },
      });

      dispatch({
        type: SET_SERVER_HEALTH_DATA,
        payload: {
          serverHealthData: response as ServerStats,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isLoggedIn) {
      fetchServerHealthData();
      interval = setInterval(fetchServerHealthData, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoggedIn]);

  const setKiteStatus = (status: boolean) => {
    dispatch({
      type: SET_KITE_CONNECT_STATUS,
      payload: {
        kiteStatus: status,
      },
    });
  };

  return (
    <ServerHealthContext.Provider
      value={{
        ...state,
        setKiteStatus,
      }}
    >
      {children}
    </ServerHealthContext.Provider>
  );
};

export const useServerHealthContext = () => {
  const context = useContext(ServerHealthContext);
  if (!context) {
    throw new Error(
      "useServerHealthContext must be used within a ServerHealthContextProvider"
    );
  }
  return context;
};
export default ServerHealthContext;
